import {useMutation, useQueryClient, useQuery, skipToken} from '@tanstack/react-query';
import {useAuth} from 'bigdatr-style';
import Segment, {SegmentDefinition} from '~/segment/data/Segment';
import {
    CreativeEmailSegmentCreate,
    CreativeEmailSegmentQuery,
    CreativeEmailSegmentUpdate,
    SegmentCreateMutation,
    SegmentListQuery,
    SegmentNzUnsubsribeMutation,
    SegmentRemoveManyMutation,
    SegmentRemoveMutation,
    SegmentSearchQuery,
    SegmentShareManyMutation,
    SegmentUpdateMutation,
    SegmentV2Query
} from './graphqlQueries';
import {SegmentSearchInput, SharedStatus} from '~/graphql-react-query/graphql';
import useClientMainAuthorizedFetch from '../clientMainAuthorisedFetch';
import {QueryStringFilters} from '~/feature/filter/types';

function getCreativeEmailSegmentQueryKey(teamId: string | null) {
    return ['creativeEmailSegment', teamId];
}

const segmentListQueryKeys = {
    segmentList: (segmentPayload: SegmentListPayload) => ['segmentLists', segmentPayload] as const
};

const segmentQueryKeys = {
    segment: (segmentPayload: SegmentPayload) => ['segment', segmentPayload] as const
};

type CreativeEmailSegmentCreateInput = {
    aiAdvertiserV1?: string[];
    category?: string[];
    emailAlert?: boolean;
    teamId: string;
};

type CreativeEmailSegmentInput = {
    id: string;
    brand?: string[];
    category?: string[];
    aiAdvertiserV1?: string[];
    emailAlert?: boolean;
};

type FollowBrandInput = {
    id: string;
    brand?: SegmentDefinition['brand'];
    category?: SegmentDefinition['category'];
    aiAdvertiserV1?: SegmentDefinition['aiAdvertiserV1'];
    emailAlert?: boolean;
};

type SegmentListPayload = {
    country?: string;
    idList?: string[];
    ignoreSharing?: boolean;
    includeDeleted?: boolean;
    requestFullPrimitives?: boolean;
};

type SegmentPayload = {
    id: string;
    includeDeleted?: boolean;
    ignoreSharing?: boolean;
};

type SegmentCreatePayload = {
    color?: string;
    country?: string;
    definitionV2?: QueryStringFilters;
    description?: string;
    emailAlert?: boolean;
    name: string;
    sharedStatus?: SharedStatus;
    userIdsWithAccess?: string[];
};

type SegmentUpdatePayload = {
    color?: string;
    definitionV2: QueryStringFilters;
    description?: string;
    emailAlert?: boolean;
    id: string;
    name: string;
    sharedStatus?: SharedStatus;
    userIdsWithAccess?: string[];
};

/**
 * creates an australian daily creative email segment and invalidates the viewer after its done.
 *
 * we invalidate the viewer because the whole app forces onboarding pages if the user isn't
 * onboarded, and that info is read from the viewer. we need to invalidate the viewer from the cache
 * (which forces a refetch), and then the app will allow users to leave onboarding
 */
export function useCreativeEmailSegmentCreate() {
    const request = useClientMainAuthorizedFetch();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (input: CreativeEmailSegmentCreateInput) => {
            const response = await request(CreativeEmailSegmentCreate, {
                input: {
                    name: Segment.secretCreativeEmailSegmentName,
                    emailAlert: true, // enable notification by default
                    definitionV2: {
                        aiAdvertiserV1: input.aiAdvertiserV1,
                        category: input.category
                    }
                }
            });
            if (!response.segmentV2?.segmentCreate)
                throw new Error('Segment not returned from api');

            // refetch viewer so users can escape onboarding
            await queryClient.invalidateQueries({queryKey: ['viewer', input.teamId]});

            return new Segment(response.segmentV2.segmentCreate);
        },
        onSuccess: (data) => {
            queryClient.setQueryData(getCreativeEmailSegmentQueryKey(data.teamId), data);
        }
    });
}

export function useCreativeEmailSegmentUpdate() {
    const request = useClientMainAuthorizedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (input: CreativeEmailSegmentInput) => {
            const response = await request(CreativeEmailSegmentUpdate, {
                input
            });
            if (!response.segmentV2?.creativeEmailSegmentUpdate)
                throw new Error('Segment not returned from api');

            return new Segment(response.segmentV2.creativeEmailSegmentUpdate);
        },
        onSuccess: (data) => {
            queryClient.setQueryData(getCreativeEmailSegmentQueryKey(data.teamId), data);
        }
    });
}

export function useCreativeEmailSegment(options?: {enabled: boolean}) {
    const authData = useAuth();
    const request = useClientMainAuthorizedFetch();
    const enabled = options?.enabled ?? true;
    return useQuery({
        queryKey: getCreativeEmailSegmentQueryKey(authData.teamId),
        queryFn: enabled
            ? async () => {
                  const response = await request(CreativeEmailSegmentQuery, {
                      input: {fullPrimitive: true}
                  });
                  if (!response.segmentV2?.creativeEmailSegment) return null;

                  return new Segment(response.segmentV2.creativeEmailSegment);
              }
            : skipToken
    });
}

export function useFollowBrand() {
    const authData = useAuth();
    const request = useClientMainAuthorizedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        // this simulates an async generator with enty
        scope: {
            id: 'followBrand'
        },
        mutationFn: async (input: FollowBrandInput) => {
            const response = await request(CreativeEmailSegmentUpdate, {
                input: {
                    id: input.id,
                    brand: input.brand?.map((e) => e.id),
                    aiAdvertiserV1: input.aiAdvertiserV1,
                    category: input.category?.map((e) => e.id),
                    emailAlert: input.emailAlert
                }
            });
            if (!response.segmentV2?.creativeEmailSegmentUpdate)
                throw new Error('Segment not returned from api');

            return new Segment(response.segmentV2.creativeEmailSegmentUpdate);
        },
        // optimistically update the daily email segment
        onMutate: async (nextPartialSegment) => {
            // Snapshot the previous value
            const cachedSegment = queryClient.getQueryData(
                getCreativeEmailSegmentQueryKey(authData.teamId)
            ) as Segment;

            const mergedSegment = new Segment({
                ...cachedSegment,
                definitionV2: {
                    ...cachedSegment?.definitionV2,
                    brand: [...(nextPartialSegment.brand || [])],
                    aiAdvertiserV1: [...(nextPartialSegment.aiAdvertiserV1 || [])]
                }
            });

            // Optimistically update to the new value
            queryClient.setQueryData(
                getCreativeEmailSegmentQueryKey(authData.teamId),
                mergedSegment
            );

            // Return a context with the previous and new todo
            return {cachedSegment};
        },
        // If the mutation fails, use the context we returned above to rollback
        onError: (err, newTodo, context) => {
            if (context?.cachedSegment) {
                queryClient.setQueryData(
                    getCreativeEmailSegmentQueryKey(authData.teamId),
                    context.cachedSegment
                );
            }
        }
    });
}

export function useSegmentCreate() {
    const request = useClientMainAuthorizedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (input: SegmentCreatePayload) => {
            const response = await request(SegmentCreateMutation, {input});
            const data = response.segmentV2;
            if (!data) throw new Error('API failed to create segment.');
            return new Segment(data.segmentCreate);
        },
        onSuccess() {
            queryClient.invalidateQueries({queryKey: segmentListQueryKeys.segmentList({})});
        }
    });
}

export function useSegmentList(payload: SegmentListPayload) {
    const request = useClientMainAuthorizedFetch();
    return useQuery({
        queryKey: segmentListQueryKeys.segmentList(payload),
        queryFn: async () => {
            const response = await request(SegmentListQuery, {input: payload});
            if (!response.segmentV2?.segmentList)
                throw new Error('API failed to create segment list.');

            const instancedSegments = response.segmentV2.segmentList.data
                .filter(
                    // narrow the type
                    (
                        filteredSegment
                    ): filteredSegment is typeof filteredSegment & {
                        emailAlert: boolean;
                        definitionV2: object;
                    } => !!filteredSegment && filteredSegment.definitionV2 != null
                )
                .map((segment) => new Segment(segment));

            return instancedSegments;
        }
    });
}

export function useSegment(payload: SegmentPayload) {
    const request = useClientMainAuthorizedFetch();
    return useQuery({
        queryKey: segmentQueryKeys.segment(payload),
        queryFn: async () => {
            const response = await request(SegmentV2Query, payload);
            if (!response.segmentV2?.segment) throw new Error('API failed to return segment.');
            const instancedSegment = new Segment(response.segmentV2?.segment);
            return instancedSegment;
        }
    });
}

export function useSegmentUpdate() {
    const request = useClientMainAuthorizedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (input: SegmentUpdatePayload) => {
            const response = await request(SegmentUpdateMutation, {input});
            const data = response.segmentV2;
            if (!data) throw new Error('API failed to update segment.');
            return data.segmentUpdate;
        },
        onSuccess(segment) {
            if (segment) {
                queryClient.setQueryData(
                    segmentQueryKeys.segment({
                        id: segment.id,
                        includeDeleted: true,
                        ignoreSharing: true
                    }),
                    segment
                );
            }
            queryClient.invalidateQueries({queryKey: segmentListQueryKeys.segmentList({})});
        }
    });
}

export function useSegmentRemove() {
    const request = useClientMainAuthorizedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: {id: string}) => {
            const response = await request(SegmentRemoveMutation, {id: payload.id});
            const data = response.segmentV2;
            if (!data) throw new Error('API failed to remove segment.');
            return data.segmentRemove;
        },
        onSuccess() {
            queryClient.invalidateQueries({queryKey: segmentListQueryKeys.segmentList({})});
        }
    });
}

export function useSegmentRemoveMany() {
    const request = useClientMainAuthorizedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: string[]) => {
            const response = await request(SegmentRemoveManyMutation, {ids: payload});
            const data = response.segmentV2;
            if (!data) throw new Error('API failed to remove segments.');
            return data.segmentRemoveMany.data;
        },
        onSuccess() {
            queryClient.invalidateQueries({queryKey: segmentListQueryKeys.segmentList({})});
        }
    });
}

export function useSegmentShareMany() {
    const request = useClientMainAuthorizedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: {
            ids: string[];
            sharedStatus: SharedStatus;
            userIdsWithAccess: string[];
        }) => {
            const response = await request(SegmentShareManyMutation, {input: payload});
            const data = response.segmentV2;
            if (!data) throw new Error('API failed to share segments.');
            return data.segmentShareMany.data;
        },
        onSuccess() {
            queryClient.invalidateQueries({queryKey: segmentListQueryKeys.segmentList({})});
        }
    });
}

export function useSegmentSearch(payload: SegmentSearchInput) {
    const request = useClientMainAuthorizedFetch();

    return useQuery({
        queryKey: ['segmentSearch', payload],
        queryFn: async (): Promise<Segment[]> => {
            const response = await request(SegmentSearchQuery, {input: payload});

            if (!response.segmentV2?.segmentSearch)
                throw new Error('API failed to return searched segment.');

            const segmentData = response.segmentV2.segmentSearch.data;

            const instancedSegment = segmentData
                .filter((data): data is NonNullable<typeof data> => Boolean(data))
                .map(
                    (data) =>
                        new Segment({
                            ...data,
                            emailAlert: data.emailAlert ?? false
                        })
                );

            return instancedSegment;
        }
    });
}

export function useNzUnsubscribe() {
    const request = useClientMainAuthorizedFetch();
    return useMutation({
        mutationFn: async (payload: {teamId: string}) => {
            const response = await request(SegmentNzUnsubsribeMutation, payload);
            const data = response.segmentV2;
            if (!data) throw new Error('API failed to unsubcribe.');
            return data.nzUnsubscribe;
        }
    });
}
